import { Link } from "react-router-dom";
import {
  EnvelopeSimple, Phone, WhatsappLogo, LinkedinLogo, YoutubeLogo, ChatCircle,
} from "@phosphor-icons/react";

const PRODUCT_LINKS = [
  { to: "/capabilities", label: "Features" },
  { to: "/product", label: "Modules" },
  { to: "/capabilities", label: "Reporting" },
  { to: "/capabilities", label: "Integrations" },
];
const HOWIT_LINKS = [
  { to: "/workflow", label: "Workflow" },
  { to: "/how-it-works", label: "Use Cases" },
  { to: "/support", label: "System Requirements" },
  { to: "/privacy", label: "Security" },
];
const RESOURCES_LINKS = [
  { to: "/resources", label: "Documentation" },
  { to: "/resources", label: "Tutorials" },
  { to: "/resources", label: "Blog" },
  { to: "/resources", label: "Case Studies" },
  { to: "/faq", label: "FAQ" },
];
const COMPANY_LINKS = [
  { to: "/about-us", label: "About Us" },
  { to: "/careers", label: "Careers" },
  { to: "/contact", label: "Contact Us" },
  { to: "/support", label: "Support" },
  { to: "/privacy", label: "Privacy Policy" },
];

export default function MarketingFooter() {
  return (
    <footer className="bg-black text-white" data-testid="marketing-footer">
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 pb-8">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-10">
          {/* Brand column */}
          <div className="col-span-2 md:col-span-1">
            <div className="inline-flex items-center bg-white rounded-lg px-4 py-3">
              <img
                src="/sres-logo-horizontal.png?v=5"
                alt="SRES · Shanvi Resource Estimation Studio"
                className="h-9 w-auto object-contain"
                draggable={false}
              />
            </div>
            <p className="mt-6 text-white/60 text-[13px] leading-relaxed max-w-xs">
              Modern resource estimation platform for geologists and mine engineers.
              Estimate defensibly, optimise smarter, report faster.
            </p>
            <div className="mt-6 flex items-center gap-2">
              <SocialIcon href="https://www.linkedin.com/" icon={LinkedinLogo} label="LinkedIn" testid="footer-social-linkedin" />
              <SocialIcon href="https://youtube.com/" icon={YoutubeLogo} label="YouTube" testid="footer-social-youtube" />
              <SocialIcon href="https://wa.me/919938872207" icon={ChatCircle} label="WhatsApp" testid="footer-social-whatsapp" />
            </div>
          </div>

          <FooterColumn title="Product" links={PRODUCT_LINKS} testid="footer-col-product" />
          <FooterColumn title="How it Works" links={HOWIT_LINKS} testid="footer-col-how" />
          <FooterColumn title="Resources" links={RESOURCES_LINKS} testid="footer-col-resources" />
          <FooterColumn title="Company" links={COMPANY_LINKS} testid="footer-col-company" />
        </div>

        {/* Bottom strip — copyright · email · phone */}
        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col md:flex-row md:items-center justify-between gap-5">
          <div className="text-[12.5px] text-white/60 leading-relaxed flex items-center gap-2.5" data-testid="footer-copyright">
            <img
              src="/sres-mark.png?v=6"
              alt=""
              aria-hidden="true"
              className="h-4 w-4 opacity-70 shrink-0"
              draggable={false}
              data-testid="footer-brand-mark"
            />
            <span>
              © {new Date().getFullYear()} SRES · All rights reserved.
              <span className="mx-2 text-white/25">•</span>
              <span className="text-white/70">A Product of Shanvi Resources.</span>
            </span>
          </div>
          <div className="flex items-center gap-5 text-[13px] flex-wrap">
            <a
              href="mailto:info@sresources.co"
              className="inline-flex items-center gap-2 text-white/80 hover:text-brand-blueLight transition-colors"
              data-testid="footer-email"
            >
              <EnvelopeSimple size={14} weight="bold" className="text-brand-blueLight" />
              info@sresources.co
            </a>
            <span className="hidden md:inline text-white/20">·</span>
            <a
              href="tel:+918079989999"
              className="inline-flex items-center gap-2 text-white/80 hover:text-brand-blueLight transition-colors font-mono tabular-nums"
              data-testid="footer-phone"
            >
              <Phone size={14} weight="bold" className="text-brand-blueLight" />
              +91 80799 89999
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterColumn({ title, links, testid }) {
  return (
    <div data-testid={testid}>
      <div className="font-heading font-bold text-white text-[15px] mb-5">{title}</div>
      <ul className="space-y-3 text-[13px]">
        {links.map((l, i) => (
          <li key={`${l.to}-${i}`}>
            <Link
              to={l.to}
              className="text-white/70 hover:text-brand-blueLight transition-colors"
            >
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SocialIcon({ href, icon: Icon, label, testid }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      aria-label={label}
      className="w-9 h-9 rounded-lg bg-white/[0.06] border border-white/10 flex items-center justify-center hover:bg-brand-blue hover:border-brand-blue transition-colors"
      data-testid={testid}
    >
      <Icon size={16} weight="bold" />
    </a>
  );
}
